import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import { DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { FeedTypeCode } from '@optools/services/operator-gateway/btc/types';
import { Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z
  .object({
    fronthaulDecoderInputLabel: z
      .string()
      .transform((value) => (value?.length === 0 ? null : value))
      .optional()
      .nullable(),
    multicastAddress: z
      .string()
      .transform((value) => (value?.length === 0 ? null : value))
      .optional()
      .nullable()
      .refine((value) => value === null || z.string().url().safeParse(value).success, {
        error: 'Multicast address must be valid udp or rtp address',
      }),
  })
  .refine((data) => data.fronthaulDecoderInputLabel || data.multicastAddress, {
    error: 'Fronthaul Decoder Input Label or Multicast Address must be provided',
    path: ['multicastAddress'],
  });
export type BroadcastOverrideFormValues = z.infer<typeof formSchema>;

export interface BroadcastOverrideFormProps {
  callSign: string;
  defaultValues?: BroadcastOverrideFormValues;
  disabled?: boolean;
  feedTypeCode: FeedTypeCode;
  gamePk: number;
  onCancel: () => void;
  onSubmit: (values: BroadcastOverrideFormValues) => void;
  region: Region;
}

export function BroadcastOverrideForm({
  callSign,
  defaultValues,
  disabled,
  feedTypeCode,
  gamePk,
  onCancel,
  onSubmit,
  region,
}: BroadcastOverrideFormProps) {
  const form = useForm<BroadcastOverrideFormValues>({
    defaultValues: {
      fronthaulDecoderInputLabel: '',
      multicastAddress: '',
      ...defaultValues,
    },
    disabled,
    resolver: zodResolver(formSchema),
  });

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-8"
        onReset={() => {
          form.reset();
          onCancel();
        }}
        onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-2">
            {region !== Region.CENTRAL && (
              <FormField
                control={form.control}
                name="fronthaulDecoderInputLabel"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>Fronthaul Decoder Input Label</FormLabel>
                      <FormControl className="w-72">
                        <Input {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}
            <FormField
              control={form.control}
              name="multicastAddress"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Multicast Address</FormLabel>
                    <FormControl className="w-72">
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex gap-1">
              <p className="opacity-70">GamePk:</p>
              <p className="font-semibold">{gamePk}</p>
            </div>
            <div className="flex gap-1">
              <p className="opacity-70">Feed Type Code:</p>
              <p className="font-semibold">{feedTypeCode}</p>
            </div>
            <div className="flex gap-1">
              <p className="opacity-70">Call Sign:</p>
              <p className="font-semibold">{callSign}</p>
            </div>
            <div className="flex gap-1">
              <p className="opacity-70">Region:</p>
              <p className="font-semibold">{region}</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={form.formState.disabled} type="reset" variant="secondary">
            Cancel
          </Button>
          <Button
            disabled={form.formState.disabled}
            loading={form.formState.disabled}
            type="submit">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
